<template>
  <div :class="['input-wrapper', { input_error: error }, { input_disabled: disabled }]">
    <div class="input__field-container">
      <input
        :id="id"
        ref="input"
        v-mask="inputMask"
        :value="currentValue"
        :disabled="disabled"
        :tabindex="disabled ? -1 : 1"
        v-bind="$attrs"
        class="input__field"
        type="tel"
        placeholder=" "
        @input="onInput($event.target.value, $event)"
        @focus="onFocus"
        @blur="onBlur"
      >

      <label
        v-if="label"
        :for="id"
        class="input__label"
      >
        {{ label }}
        <span v-if="required" class="input__label-required">*</span>
      </label>
    </div>

    <transition name="slide-from-top">
      <span v-if="error && errorMsg" class="input__error-msg">
        {{ errorMsg }}
        <ErrorIcon />
      </span>
    </transition>
  </div>
</template>

<script>
  import ErrorIcon from '@/assets/img/icons/error.svg?inline'
  import { mask } from 'vue-the-mask'
  import { mapGetters } from 'vuex'
  import { isMd } from '@/config'

  export default {
    name: 'BaseInputTel',
    directives: { mask },
    components: { ErrorIcon },
    inheritAttrs: false,
    props: {
      name: { type: String, default: '' },
      value: { type: String, default: '' },
      label: { type: String, default: '' },
      errorMsg: { type: String, default: '' },
      disabled: { type: Boolean, default: false },
      minLength: { type: Number, default: isMd ? 9 : 10 },
      maxLength: { type: Number, default: isMd ? 12 : 13 }
    },
    data () {
      return {
        currentValue: this.value,
        error: false
      }
    },
    computed: {
      ...mapGetters(['getPhoneType']),
      id () {
        return `input-${this._uid}`
      },
      required () {
        return 'required' in this.$attrs && this.$attrs.required
      },
      phonePrefix () {
        return this.getPhoneType === '+373' ? '+373' : '+380'
      },
      inputMask () {
        if (this.getPhoneType === '+373') {
          return '+###########'
        }
        return '+380-##-###-##-##'
      }
    },
    watch: {
      value (newVal) {
        this.currentValue = newVal
        if (!newVal) this.clearError()
      },
      errorMsg: {
        handler (msg) {
          this.error = !!msg
        },
        immediate: true
      }
    },
    methods: {
      onFocus: function () {
        this.clearError()
        if (!this.currentValue) {
          this.currentValue = this.phonePrefix
          this.$emit('input', this.phonePrefix)
        }
      },
      onInput: function (value) {
        this.currentValue = value
        this.clearError()
        this.$emit('input', value)
      },
      onBlur: function () {
        const raw = (this.currentValue || '').replace(/[\+\-\s]/g, '')
        const prefixRaw = this.phonePrefix.replace(/[\+\-\s]/g, '')
        if (!raw || raw === prefixRaw) {
          this.currentValue = ''
          this.$refs.input.value = ''
          this.$emit('input', '')
          this.clearError()
          return
        }
        this.clearError()
        this.$emit('input', this.currentValue)
      },
      clearError: function () {
        this.error = false
      }
    }
  }
</script>

<style lang="scss" scoped>
$padding-for-error: 28px;

input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
  box-shadow: 0 0 0 30px $c-gray-light inset !important;
}

.input {
  &-wrapper {
    position: relative;
    padding-bottom: $padding-for-error;
  }

  &_disabled {
    pointer-events: none;

    .input__field {
      background: $c-gray-4;
    }
  }

  &__label {
    position: absolute;
    z-index: 1;
    top: 50%;
    left: 20px;
    transform: translateY(-50%);
    display: inline-block;
    font-family: "ToyotaType-Book", Arial, Helvetica, sans-serif;
    font-size: 16px;
    line-height: 1.2;
    color: $c-gray-7;
    transition: 0.3s;
    pointer-events: none;

    &-required {
      position: relative;
      top: -3px;
      color: $c-errorDark !important;
      font-size: 13px;
      right: -3px;
    }
  }

  &__field {
    display: block;
    width: 100%;
    padding: 27.5px 16px 8px;
    font-family: "ToyotaType-Book", Arial, Helvetica, sans-serif;
    font-size: 16px;
    line-height: 1;
    outline: none;
    caret-color: $c-gray-dark;
    appearance: none !important;
    -webkit-appearance: none !important;
    border-radius: 0 !important;
    transition: 0.3s;
    border: 1px solid $c-gray-4;

    &:invalid {
      box-shadow: none;
    }

    &-container {
      position: relative;
    }

    @include hover {
      border: 1px solid $c-gray-dark;
    }

    &:focus {
      border: 1px solid $c-gray-dark;
    }

    &:focus ~ .input__label,
    &:not(:placeholder-shown) ~ .input__label {
      top: 11px;
      left: 20px;
      font-size: 13px;
      transform: translateY(0);

      @include mobile {
        font-size: 11px;
      }
    }
  }

  &__error-msg {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: absolute;
    left: 0;
    font-size: 12px;
    color: $c-errorDark;
    height: 20px;
    bottom: 4px;
    width: calc(100% - 30px);

    @include tablet {
      padding-top: 3px;
      position: static;
      min-height: 30px;
      height: auto;

      &.slide-from-top-enter-active,
      &.slide-from-top-leave-active {
        transition: none;
      }

      &.slide-from-top-enter,
      &.slide-from-top-leave-to {
        opacity: 1;
        transform: none;
      }
    }

    img,
    svg {
      display: flex;
      min-width: 14px;
      min-height: 15px;
      width: 14px;
      height: 15px;
      object-fit: contain;
    }
  }

  &_error {
    .input {
      &__field {
        border: 1px solid $c-errorDark;
      }
    }
  }
}
</style>
